import { dispatchNewEvent } from "@/utils/analytics/trigger/analyticsTrigger";
import Form from "@JohnLewisPartnership/checkout-forms/dist/components/form-simple/Form";
import LoadingSpinner from "@/components/loading-spinner/LoadingSpinner";
import axios from "axios";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { getPreferredResolutionDescription } from "./PreferredResolution";
import styles from "./email-form.module.scss";
import useAttachments from "./useAttachments";
import { getEmailFormElements } from "./utils";
import { MONETATE_EVENT_NAMES, trackMonetateEvent } from "@/context/MonetateContext";
import { emailTopicsMappingToBreadcrumbsAndSolutionTags } from "../email-questions/utils";

function EmailForm({
  subject,
  shop = null,
  fields,
  imageMessage = "",
  onSubmitted = () => {},
  id = "",
  source,
  submitButton,
  preferredRes = "",
  emailTopic = "",
}) {
  const router = useRouter();
  const orderId = router.query?.orderId || "";
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);
  const { attachments, attachmentError, handleAttachmentAdded, handleAttachmentRemoved } = useAttachments();
  const [intialData] = useState({
    loading: false, // typeof window !== "undefined" && !!window.auth0,
    data: { orderNumber: orderId },
  });

  useEffect(() => {
    dispatchNewEvent("dataStateChange", {
      action: "email.formLoaded",
      topic: `${subject}-${shop ? "Store" : "Online"}`,
    });
  }, []);

  const handleSubmit = (data, onComplete) => {
    // Hack as we are using form fields to display steps. Each step component has undefined has the id/name
    // eslint-disable-next-line
    let topic = "None";
    delete data["undefined"];
    // eslint-disable-next-line no-param-reassign
    data.webFormSubject = subject;
    if (shop) {
      // eslint-disable-next-line no-param-reassign
      data.purchaseLocation = shop;
    }
    if (preferredRes) {
      data.description = `${getPreferredResolutionDescription(preferredRes)}\n\n${data.description}`;
      topic = preferredRes;
    }
    // eslint-disable-next-line no-param-reassign
    data.imageUpload = undefined;
    // eslint-disable-next-line no-param-reassign
    if (source) data.source = source;
    const formData = new FormData();
    formData.append("caseData", JSON.stringify(data));
    attachments.forEach(attachment => formData.append("file", attachment.file));

    setSubmitting(!!attachments.length);

    axios
      .post(`${process.env.NEXT_PUBLIC_API_DOMAIN}/customer-help/email-us/v1/case`, formData)
      .then(response => {
        // Track the email topics that are eligible for live chat to understand the impact of live chat on email submission rates as part of the AB6553 experiment
        const emailTopicsEligibleForLiveChat = Object.keys(emailTopicsMappingToBreadcrumbsAndSolutionTags);
        const eligibleForLiveChat = emailTopicsEligibleForLiveChat.includes(emailTopic);

        if (eligibleForLiveChat) {
          trackMonetateEvent(MONETATE_EVENT_NAMES.AB6553EmailFormSubmitted);
        }

        onSubmitted(
          response.data.caseNumber,
          data.emailAddress,
          data.webFormSubject,
          shop,
          response.data.warnings && response.data.warnings.length > 0,
        );

        setSubmitting(false);

        dispatchNewEvent("dataStateChange", {
          action: "email.submit",
          type: "success",
          topic: topic,
          imageUpload: attachments.length > 0, // false depending whether an image was uploaded with the email or not.
        });
        onComplete();
      })
      .catch(error => {
        let body;
        let title = "Sorry, your email wasn't sent.";
        if (error.response) {
          // Server side issue
          body = "We've had a technical problem. Please try again later.";
        } else if (error.request) {
          // Connectivity / Timeout
          title = "Sorry, we've lost connection.";
          body = "Please try submitting the form again";
        } else {
          // Something happened in setting up the request that triggered an Error
          body = "There's been a technical problem. Please try again later.";
        }

        setServerError({ title, body });
        setSubmitting(false);
        dispatchNewEvent("dataStateChange", {
          action: "email.submit",
          type: "failure",
          topic: topic,
          errorMessage:
            (error.response?.data?.messages && error.response.data.messages.join("|")) || "email submit failure",
          errorCode: error.response?.data.code,
        });
        onComplete();
      });
  };

  const elements = getEmailFormElements(fields, shop, {
    handleAttachmentAdded,
    handleAttachmentRemoved,
    attachments,
    attachmentError,
    imageMessage,
  });

  if (intialData.loading === true) {
    return (
      <div className={styles.center}>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className={styles.formWrapper}>
      <Form
        fields={elements}
        initialValues={intialData.data}
        onSubmit={handleSubmit}
        serverError={serverError}
        key={id}
        submitButton={submitButton}
      />
      {submitting ? (
        <p className={styles["email-form__submit-message"]}>
          Sending your email. If you&apos;ve attached large images, it may take a little while.
        </p>
      ) : null}
    </div>
  );
}

export default EmailForm;
